import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoginForm } from '../components/forms/LoginForm';
import { loginApi, totpVerifyApi } from '../api/auth';

const IS_SHADOW = typeof window !== 'undefined' && (
  window.location.hostname.startsWith('shadow.')
  || window.location.hostname === 'app.intrlock.io'
  || window.location.hostname === 'admin.intrlock.io'
);

type Step = 'password' | 'totp';

export const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('password');
  const [totpToken, setTotpToken] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const completeLogin = (token: string, userObj: any, enrollmentRequired?: boolean) => {
    const userData = {
      id: userObj.id,
      email: userObj.email,
      name: [userObj.first_name, userObj.last_name].filter(Boolean).join(' ') || userObj.email,
      role: userObj.role || 'customer',
      // Auth-v2 returns dealer_id; legacy UI scoping still reads integrator_id.
      integrator_id: userObj.dealer_id ?? userObj.integrator_id,
      dealer_id: userObj.dealer_id ?? userObj.integrator_id,
      customer_id: userObj.customer_id,
      property_id: userObj.property_id,
      property_ids: userObj.property_ids,
      stripe_customer_id: userObj.stripe_customer_id,
    };
    login(token, userData);
    navigate(enrollmentRequired ? '/security?enroll=1' : '/dashboard');
  };

  const handleLogin = async (email: string, password: string) => {
    setError(''); setLoading(true);
    try {
      const res = await loginApi(email, password);
      if ('totp_required' in res && res.totp_required) {
        setTotpToken(res.totp_token);
        setStep('totp');
      } else {
        completeLogin(res.token, res.user, !!res.totp_enrollment_required);
      }
    } catch (e: any) {
      setError(e?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await totpVerifyApi(totpToken, code.trim(), trustDevice);
      completeLogin(res.token, res.user, !!res.totp_enrollment_required);
    } catch (e: any) {
      setError(e?.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#101010' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block">
            <img
              src="/intrlock-banner-dark.png"
              alt="Intrlock"
              className="block h-60 w-auto object-contain max-w-full"
            />
            {!IS_SHADOW && <p className="text-white -mt-[95px] text-base font-medium text-left pl-[41%]">cloud-based intercom</p>}
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur shadow-2xl rounded-2xl p-8 border border-gray-100">
          {step === 'password' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">Sign in to your account</h2>
              <LoginForm onSubmit={handleLogin} error={error} />
              <p className="text-center text-sm text-gray-500 mt-6">
                Don&apos;t have an account?{' '}
                <a className="inline-flex min-h-[44px] items-center text-emerald-700 hover:underline font-medium" href="https://www.intrlock.io/signup">
                  Create one
                </a>
              </p>
            </>
          )}
          {step === 'totp' && (
            <form onSubmit={handleTotpSubmit}>
              <h2 className="text-xl font-semibold text-gray-900 mb-1 text-center">Two-factor authentication</h2>
              <p className="text-sm text-gray-500 mb-6 text-center">
                Enter the 6-digit code from your authenticator app, or a backup code.
              </p>
              <input
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                placeholder="123 456 or XXXXX-XXXXX"
                inputMode="text"
                autoComplete="one-time-code"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none text-center text-xl tracking-widest font-mono"
              />
              <label className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                Trust this device for 30 days
              </label>
              {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
              <button
                type="submit"
                disabled={loading || code.length < 5}
                className="w-full mt-4 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-700"
              >
                {loading ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('password'); setCode(''); setError(''); }}
                className="w-full min-h-[44px] mt-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-white/70 text-sm mt-6">&copy; 2026 Intrlock. All rights reserved.</p>
      </div>
    </div>
  );
};
